import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UnmappedIcon,
  TableView as TableViewIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

interface ExcelData {
  headers: string[];
  rows: string[][];
  totalRows: number;
}

interface ColumnMappingData {
  [key: string]: string;
}

interface ManualValueData {
  [key: string]: string;
}

interface ColumnMappingProps {
  excelData: ExcelData;
  mappings: ColumnMappingData;
  onMappingChange: (mappings: ColumnMappingData) => void;
}

interface FieldGroup {
  title: string;
  fields: Array<{
    key: string;
    label: string;
    required?: boolean;
    description?: string;
  }>;
  legNumber?: number;
}

const defaultFieldGroups: FieldGroup[] = [
  {
    title: 'Shipment Information',
    fields: [
      { key: 'shipmentDate', label: 'Shipment Date', required: true, description: 'Date when shipment was created' },
      { key: 'shipmentId', label: 'Shipment ID', required: true, description: 'Unique shipment identifier' },
      { key: 'customerName', label: 'Customer Name', description: 'Name of the customer' },
      { key: 'customerRef', label: 'Sub Customer Code', description: 'Sub customer code' },
    ],
  },
  {
    title: 'Package Details',
    fields: [
      { key: 'weight', label: 'Weight (kg)', required: true, description: 'Total weight of the shipment' },
      { key: 'packages', label: 'Number of Packages', description: 'Total number of packages' },
      { key: 'cooling', label: 'Cooling', description: 'Refrigerated shipment (true/false or yes/no)' },
    ],
  },
];

const ColumnMappingNew: React.FC<ColumnMappingProps> = ({
  excelData,
  mappings,
  onMappingChange,
}) => {
  const [fieldGroups, setFieldGroups] = useState<FieldGroup[]>(defaultFieldGroups);
  const [fullDataDialogOpen, setFullDataDialogOpen] = useState(false);
  const [customFieldDialog, setCustomFieldDialog] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [manualValues, setManualValues] = useState<ManualValueData>({});
  const [inputModes, setInputModes] = useState<{[key: string]: 'column' | 'manual'}>({});

  // Helper to get the mapped column for a field (reverse lookup)
  const getMappedColumnForField = (fieldKey: string): string => {
    // Find which column maps to this fieldKey
    const column = Object.keys(mappings).find(key => mappings[key] === fieldKey);
    return column || '';
  };

  // Get sample data for a column
  const getColumnSampleData = (columnIndex: number) => {
    return excelData.rows
      .slice(0, 3)
      .map(row => row[columnIndex] || '')
      .filter(cell => cell.trim() !== '')
      .slice(0, 2);
  };

  // Get column options with sample data
  const getColumnOptions = () => {
    return excelData.headers.map((header, index) => {
      const sampleData = getColumnSampleData(index);
      const displayText = sampleData.length > 0
        ? `Col ${index + 1} (${header}): ${sampleData[0]}`
        : `Col ${index + 1} (${header})`;

      return {
        value: String(index), // Use column index as key
        label: displayText,
        header,
        sampleData,
        index,
      };
    });
  };

  const handleMappingChange = (fieldKey: string, columnValue: string) => {
    const newMappings = { ...mappings };

    // Remove ALL traces of this field from mappings (both directions)
    // 1. Remove where this field is the VALUE (column -> field)
    Object.keys(newMappings).forEach(key => {
      if (newMappings[key] === fieldKey) {
        delete newMappings[key];
      }
    });

    // 2. Remove where this field is the KEY (old wrong direction: field -> column)
    if (newMappings[fieldKey]) {
      delete newMappings[fieldKey];
    }

    if (columnValue === '') {
      // Also clear manual value and reset to column mode
      const newManualValues = { ...manualValues };
      delete newManualValues[fieldKey];
      setManualValues(newManualValues);

      const newInputModes = { ...inputModes };
      delete newInputModes[fieldKey];
      setInputModes(newInputModes);
    } else {
      // CORRECT DIRECTION: columnValue -> fieldKey (e.g., 'Column9' -> 'weight')
      newMappings[columnValue] = fieldKey;
      // Set to column mode when selecting a column
      setInputModes(prev => ({ ...prev, [fieldKey]: 'column' }));
    }
    onMappingChange(newMappings);
  };

  const handleManualValueChange = (fieldKey: string, value: string) => {
    setManualValues(prev => ({ ...prev, [fieldKey]: value }));
    // Set mapping to indicate this is a manual value
    const newMappings = { ...mappings };

    // Remove ALL traces of this field from mappings (both directions)
    Object.keys(newMappings).forEach(key => {
      if (newMappings[key] === fieldKey) {
        delete newMappings[key];
      }
    });
    if (newMappings[fieldKey]) {
      delete newMappings[fieldKey];
    }

    // Add manual value mapping (MANUAL:value -> fieldKey)
    newMappings[`MANUAL:${value}`] = fieldKey;
    onMappingChange(newMappings);
  };

  const toggleInputMode = (fieldKey: string, mode: 'column' | 'manual') => {
    setInputModes(prev => ({ ...prev, [fieldKey]: mode }));

    const newMappings = { ...mappings };

    // Remove ALL traces of this field from mappings (both directions)
    Object.keys(newMappings).forEach(key => {
      if (newMappings[key] === fieldKey) {
        delete newMappings[key];
      }
    });
    if (newMappings[fieldKey]) {
      delete newMappings[fieldKey];
    }

    if (mode === 'manual') {
      // Clear column mapping when switching to manual
      onMappingChange(newMappings);
    } else {
      // Clear manual value when switching to column
      const newManualValues = { ...manualValues };
      delete newManualValues[fieldKey];
      setManualValues(newManualValues);

      onMappingChange(newMappings);
    }
  };

  const addTransportLeg = () => {
    const legNumber = fieldGroups.filter(group => group.legNumber !== undefined).length + 1;
    const newLeg: FieldGroup = {
      title: `Transport Leg ${legNumber}`,
      legNumber,
      fields: [
        { key: `originCountry_leg${legNumber}`, label: 'Origin Country', required: true },
        { key: `originCity_leg${legNumber}`, label: 'Origin City', required: true },
        { key: `originZip_leg${legNumber}`, label: 'Origin ZIP' },
        { key: `destinationCountry_leg${legNumber}`, label: 'Destination Country', required: true },
        { key: `destinationCity_leg${legNumber}`, label: 'Destination City', required: true },
        { key: `destinationZip_leg${legNumber}`, label: 'Destination ZIP' },
        { key: `transportMode_leg${legNumber}`, label: 'Transport Mode', required: true },
        { key: `distance_leg${legNumber}`, label: 'Distance (km)' },
        { key: `vehicleType_leg${legNumber}`, label: 'Vehicle Type' },
        { key: `fuelType_leg${legNumber}`, label: 'Fuel Type' },
      ],
    };
    setFieldGroups([...fieldGroups, newLeg]);
  };

  const removeTransportLeg = (legNumber: number) => {
    // Remove mappings for this leg
    // Since mappings are reversed (column -> field), we need to check the values
    const newMappings = { ...mappings };
    Object.keys(newMappings).forEach(key => {
      if (newMappings[key].includes(`_leg${legNumber}`)) {
        delete newMappings[key];
      }
    });
    onMappingChange(newMappings);

    // Remove field group
    setFieldGroups(fieldGroups.filter(group => group.legNumber !== legNumber));
  };

  const addCustomField = () => {
    if (!newFieldName.trim()) return;
    
    const customGroup = fieldGroups.find(g => g.title === 'Custom Fields');
    const fieldKey = `custom_${newFieldName.toLowerCase().replaceAll(/\s+/g, '_')}`;
    
    if (customGroup) {
      customGroup.fields.push({
        key: fieldKey,
        label: newFieldName,
        description: 'Custom field',
      });
      setFieldGroups([...fieldGroups]);
    } else {
      const newCustomGroup: FieldGroup = {
        title: 'Custom Fields',
        fields: [{
          key: fieldKey,
          label: newFieldName,
          description: 'Custom field',
        }],
      };
      setFieldGroups([...fieldGroups, newCustomGroup]);
    }
    
    setNewFieldName('');
    setCustomFieldDialog(false);
  };

  const getMappedCount = () => Object.keys(mappings).length;
  const columnOptions = getColumnOptions();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Column Mapping
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Map your Excel columns to the required fields. View your data first, then drag columns or use dropdowns.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => setFullDataDialogOpen(true)}
          >
            Preview ({getMappedCount()} mapped)
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setCustomFieldDialog(true)}
          >
            Add Custom Field
          </Button>
        </Box>
      </Box>

      {/* Sample Data Preview */}
      <Card sx={{ mb: 3 }}>
        <CardHeader 
          title="Your Data Preview"
          subheader={`${excelData.totalRows} total rows • Showing first 5 rows`}
        />
        <CardContent sx={{ pt: 0 }}>
          <TableContainer sx={{ maxHeight: 200 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {excelData.headers.map((header, index) => (
                    <TableCell key={header} sx={{
                      backgroundColor: 'primary.main',
                      color: 'white',
                      fontWeight: 'bold',
                      minWidth: 150,
                    }}>
                      {header}
                      <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
                        Column{index}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {excelData.rows.slice(0, 5).map((row, rowIndex) => (
                  <TableRow key={row.join('|')} hover>
                    {row.map((cell, cellIndex) => (
                      <TableCell key={excelData.headers[cellIndex]} sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" noWrap>
                          {cell || '-'}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Available Columns */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: 'fit-content', position: 'sticky', top: 20 }}>
            <CardHeader 
              title="Available Columns"
              subheader="Drag these to fields or use dropdowns"
            />
            <CardContent sx={{ pt: 0 }}>
              <Stack spacing={1}>
                {columnOptions.map((column) => (
                  <Paper
                    key={column.value}
                    sx={{
                      p: 2,
                      cursor: 'grab',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        borderColor: 'primary.main',
                        backgroundColor: 'action.hover',
                      },
                    }}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', column.value);
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DragIcon color="action" />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle1" noWrap>
                          {column.header}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Column{column.index} • {column.sampleData.length > 0 ? `e.g., ${column.sampleData[0]}` : 'No data'}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Mapping Fields */}
        <Grid item xs={12} lg={8}>
          <Stack spacing={2}>
            {fieldGroups.map((group) => (
              <Card key={group.title}>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h6">{group.title}</Typography>
                      <Badge
                        badgeContent={group.fields.filter(f => getMappedColumnForField(f.key)).length}
                        color="primary"
                        showZero
                      >
                        <Chip
                          label={`${group.fields.filter(f => getMappedColumnForField(f.key)).length}/${group.fields.length} mapped`}
                          size="small"
                          color={group.fields.every(f => !f.required || getMappedColumnForField(f.key)) ? "success" : "default"}
                        />
                      </Badge>
                      {group.legNumber && (
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => {
                            if (group.legNumber !== undefined) {
                              removeTransportLeg(group.legNumber);
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  }
                />
                <CardContent sx={{ pt: 0 }}>
                  <Grid container spacing={2}>
                    {group.fields.map((field) => (
                      <Grid item xs={12} sm={6} key={field.key}>
                        <Paper
                          sx={{
                            p: 2,
                            border: '2px dashed',
                            borderColor: getMappedColumnForField(field.key) ? 'success.main' : 'divider',
                            backgroundColor: getMappedColumnForField(field.key) ? 'success.50' : 'background.paper',
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const columnValue = e.dataTransfer.getData('text/plain');
                            handleMappingChange(field.key, columnValue);
                          }}
                          onDragOver={(e) => e.preventDefault()}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                            {getMappedColumnForField(field.key) ? (
                              <CheckCircleIcon color="success" fontSize="small" />
                            ) : (
                              <UnmappedIcon color="disabled" fontSize="small" />
                            )}
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {field.label}
                                {field.required && (
                                  <Chip label="Required" size="small" color="error" variant="outlined" />
                                )}
                              </Typography>
                              {field.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {field.description}
                                </Typography>
                              )}
                            </Box>
                            {getMappedColumnForField(field.key) && (
                              <IconButton
                                size="small"
                                onClick={() => handleMappingChange(field.key, '')}
                              >
                                <ClearIcon />
                              </IconButton>
                            )}
                          </Box>

                          {/* Input Mode Toggle */}
                          <Box sx={{ mb: 2 }}>
                            <ToggleButtonGroup
                              value={inputModes[field.key] || 'column'}
                              exclusive
                              onChange={(_, newMode) => {
                                if (newMode !== null) {
                                  toggleInputMode(field.key, newMode);
                                }
                              }}
                              size="small"
                              fullWidth
                            >
                              <ToggleButton value="column">
                                <TableViewIcon sx={{ mr: 0.5 }} />
                                From Column
                              </ToggleButton>
                              <ToggleButton value="manual">
                                <EditIcon sx={{ mr: 0.5 }} />
                                Manual Value
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </Box>

                          {/* Column Selection */}
                          {(!inputModes[field.key] || inputModes[field.key] === 'column') && (
                            <FormControl fullWidth size="small">
                              <InputLabel>Select Column</InputLabel>
                              <Select
                                value={(() => {
                                  const mappedColumn = getMappedColumnForField(field.key);
                                  return mappedColumn?.startsWith('MANUAL:') ? '' : (mappedColumn || '');
                                })()}
                                label="Select Column"
                                onChange={(e) => handleMappingChange(field.key, e.target.value)}
                              >
                                <MenuItem value="">
                                  <em>No mapping</em>
                                </MenuItem>
                                {columnOptions.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    <Box>
                                      <Typography variant="body2">{option.label}</Typography>
                                      {option.sampleData.length > 1 && (
                                        <Typography variant="caption" color="text.secondary">
                                          Also: {option.sampleData.slice(1).join(', ')}
                                        </Typography>
                                      )}
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}

                          {/* Manual Value Input */}
                          {inputModes[field.key] === 'manual' && (
                            <FormControl fullWidth size="small">
                              <TextField
                                label="Manual Value (applied to all rows)"
                                value={manualValues[field.key] || ''}
                                onChange={(e) => handleManualValueChange(field.key, e.target.value)}
                                size="small"
                                placeholder="Enter value for all rows"
                                helperText="This value will be used for all rows in the file"
                              />
                            </FormControl>
                          )}

                          {/* Mapping Status Chip */}
                          {(() => {
                            const mappedColumn = getMappedColumnForField(field.key);
                            if (!mappedColumn) return null;
                            const isManual = mappedColumn.startsWith('MANUAL:');
                            let chipLabel: string;
                            if (isManual) {
                              chipLabel = `Manual Value: ${mappedColumn.replace('MANUAL:', '')}`;
                            } else {
                              let option: typeof columnOptions[number] | undefined;
                              for (const o of columnOptions) {
                                if (o.value === mappedColumn) {
                                  option = o;
                                  break;
                                }
                              }
                              const sampleData = option?.sampleData?.[0];
                              chipLabel = `Mapped to: ${option?.header}${sampleData ? ' (' + sampleData + ')' : ''}`;
                            }
                            return (
                              <Chip
                                label={chipLabel}
                                size="small"
                                color={isManual ? 'info' : 'success'}
                                sx={{ mt: 1 }}
                                icon={isManual ? <EditIcon /> : <TableViewIcon />}
                              />
                            );
                          })()}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            ))}

            {/* Add Transport Leg Button */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addTransportLeg}
              sx={{ alignSelf: 'center' }}
            >
              Add Transport Leg
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* Full Data Dialog */}
      <Dialog
        open={fullDataDialogOpen}
        onClose={() => setFullDataDialogOpen(false)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          Complete Data Preview
          <Typography variant="body2" color="text.secondary">
            {excelData.totalRows} total rows • Showing first 50 rows
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {excelData.headers.map((header, index) => (
                    <TableCell key={header} sx={{ 
                      backgroundColor: 'primary.main', 
                      color: 'white',
                      fontWeight: 'bold',
                      minWidth: 150,
                    }}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {excelData.rows.slice(0, 50).map((row, rowIndex) => (
                  <TableRow key={row.join('|')} hover>
                    {row.map((cell, cellIndex) => (
                      <TableCell key={excelData.headers[cellIndex]}>
                        {cell || '-'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFullDataDialogOpen(false)} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Custom Field Dialog */}
      <Dialog open={customFieldDialog} onClose={() => setCustomFieldDialog(false)}>
        <DialogTitle>Add Custom Field</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Field Name"
            fullWidth
            variant="outlined"
            value={newFieldName}
            onChange={(e) => setNewFieldName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomFieldDialog(false)} variant="outlined">Cancel</Button>
          <Button onClick={addCustomField} variant="contained">Add Field</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ColumnMappingNew;